import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { MoreVertical, Edit, Trash2, Eye } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

interface MediaListViewProps {
  media: any[];
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onEdit: (media: any) => void;
  onDelete: (id: string) => void;
  onPreview: (media: any) => void;
}

export const MediaListView = ({
  media,
  selectedIds,
  onToggleSelect,
  onEdit,
  onDelete,
  onPreview,
}: MediaListViewProps) => {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12"></TableHead>
          <TableHead className="w-20">Preview</TableHead>
          <TableHead>Filename</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Dimensions</TableHead>
          <TableHead>Uploaded</TableHead>
          <TableHead className="w-12"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {media.map((item) => {
          const imageUrl = supabase.storage
            .from('media-library')
            .getPublicUrl(item.storage_path).data.publicUrl;

          return (
            <TableRow key={item.id}>
              <TableCell>
                <Checkbox
                  checked={selectedIds.includes(item.id)}
                  onCheckedChange={() => onToggleSelect(item.id)}
                />
              </TableCell>
              <TableCell>
                <button
                  onClick={() => onPreview(item)}
                  className="hover:opacity-80 transition-opacity"
                >
                  <img
                    src={imageUrl}
                    alt={item.alt_text || item.filename}
                    className="w-16 h-16 object-cover rounded"
                  />
                </button>
              </TableCell>
              <TableCell className="font-medium">
                {item.title || item.filename}
              </TableCell>
              <TableCell>{formatFileSize(item.file_size)}</TableCell>
              <TableCell>
                {item.width && item.height ? `${item.width} × ${item.height}` : '-'}
              </TableCell>
              <TableCell>
                {new Date(item.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onPreview(item)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEdit(item)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => onDelete(item.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
